import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faCheck,
  faCircleCheck,
  faListCheck,
  faListOl,
  faSitemap,
  faVial,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";
import LessonAiProgressPanel from "../teacher/LessonAiProgressPanel";
import {
  LESSON_AI_GENERATE_PHASES,
  lessonAiPhaseAtElapsed,
  type LessonAiProgressUpdate,
} from "../teacher/lesson-ai-progress";
import { ReadOnlyPythonCode } from "../student-ide/ReadOnlyPythonCode";
import { LessonIcon } from "../teacher/LessonIcon";
import type { LessonAiDraft } from "../auth/api-client";
import { celebratePassing } from "../student-ide/confettiCelebration";
import {
  DEMO_AI_DRAFT,
  DEMO_AI_PROMPT,
  DEMO_AI_PROMPT_2,
  DEMO_COHORT_PLAN,
  DEMO_PLAN_INSERT_AFTER,
  DEMO_PLAN_INSERTED,
  DEMO_STUDENT_CHECKS,
  DEMO_STUDENT_LESSON_TITLE,
  DEMO_STUDENT_WHALE_CODE_BASE,
  DEMO_STUDENT_WHALE_TYPING,
} from "./demoData";

type Stage = "typing" | "generating" | "reveal";

interface AiScene {
  readonly kind: "ai";
  readonly promptLabel: string;
  readonly prompt: string;
  readonly buttonLabel: string;
  readonly buttonIcon: typeof faWandMagicSparkles;
  readonly reveal: "draft" | "plan";
  readonly holdMs: number;
}

interface StudentScene {
  readonly kind: "student";
  readonly promptLabel: string;
  readonly codeBase: string;
  readonly codeTyping: string;
  readonly buttonLabel: string;
  readonly buttonIcon: typeof faCircleCheck;
  readonly reveal: "checks";
  readonly holdMs: number;
}

type Scene = AiScene | StudentScene;

const HOLD_MS = 10000;

const SCENES: readonly Scene[] = [
  {
    kind: "ai",
    promptLabel: "Describe the lesson you want",
    prompt: DEMO_AI_PROMPT,
    buttonLabel: "Generate lesson",
    buttonIcon: faWandMagicSparkles,
    reveal: "draft",
    holdMs: HOLD_MS,
  },
  {
    kind: "ai",
    promptLabel: "Evolve this cohort's plan",
    prompt: DEMO_AI_PROMPT_2,
    buttonLabel: "Evolve plan",
    buttonIcon: faSitemap,
    reveal: "plan",
    holdMs: HOLD_MS,
  },
  {
    kind: "student",
    promptLabel: "A student finishes the lesson",
    codeBase: DEMO_STUDENT_WHALE_CODE_BASE,
    codeTyping: DEMO_STUDENT_WHALE_TYPING,
    buttonLabel: "Check My Work",
    buttonIcon: faCircleCheck,
    reveal: "checks",
    holdMs: HOLD_MS,
  },
];

const STUDENT_WORK_MS = 1900;

const LAST_PHASE_MS = LESSON_AI_GENERATE_PHASES[LESSON_AI_GENERATE_PHASES.length - 1]!.minMs;
/** Virtual ms advanced per real tick; compresses the ~30s real flow into a short loop. */
const VIRTUAL_STEP_MS = 650;
const TICK_MS = 110;
const TYPE_STEP_MS = 34;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

const FINISHED_PROGRESS: LessonAiProgressUpdate = lessonAiPhaseAtElapsed(
  LESSON_AI_GENERATE_PHASES,
  LAST_PHASE_MS
);

/**
 * Looping, self-running showcase of the AI lesson studio. Scene 1 generates a
 * lesson from a prompt; scene 2 evolves a specific cohort's plan by generating a
 * targeted lesson and inserting it after the first class exercise. Respects
 * prefers-reduced-motion (static finished state, no looping).
 */
export default function AiLessonAnimation() {
  const reduced = useMemo(prefersReducedMotion, []);
  const rootRef = useRef<HTMLDivElement>(null);
  const [onScreen, setOnScreen] = useState(true);
  const [sceneIndex, setSceneIndex] = useState(0);
  const [stage, setStage] = useState<Stage>(reduced ? "reveal" : "typing");
  const [typed, setTyped] = useState(
    reduced && SCENES[0]!.kind === "ai" ? SCENES[0].prompt : ""
  );
  const [submitPressed, setSubmitPressed] = useState(false);
  const confettiFiredRef = useRef(false);
  const [progress, setProgress] = useState<LessonAiProgressUpdate>(() =>
    lessonAiPhaseAtElapsed(LESSON_AI_GENERATE_PHASES, 0)
  );

  const scene = SCENES[sceneIndex]!;

  // Pause the loop while the demo is scrolled out of view; resume when it returns.
  useEffect(() => {
    const el = rootRef.current;
    if (reduced || !el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setOnScreen(entry?.isIntersecting ?? true),
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [reduced]);

  const onStudentChecksComplete = useCallback(() => {
    if (reduced || !onScreen || confettiFiredRef.current) return;
    confettiFiredRef.current = true;
    celebratePassing();
  }, [reduced, onScreen]);

  useEffect(() => {
    if (stage !== "reveal" || scene.kind !== "student") {
      confettiFiredRef.current = false;
    }
  }, [stage, scene.kind]);

  useEffect(() => {
    if (reduced || !onScreen) return;

    let cancelled = false;
    let current = 0;
    const timers: number[] = [];

    const after = (ms: number, fn: () => void) => {
      timers.push(window.setTimeout(fn, ms));
    };

    const runTyping = () => {
      if (cancelled) return;
      const active = SCENES[current]!;
      setSceneIndex(current);
      setStage("typing");
      setTyped("");
      setSubmitPressed(false);

      if (active.kind === "student") {
        let i = 0;
        const suffix = active.codeTyping;
        const typeNext = () => {
          if (cancelled) return;
          i += 1;
          setTyped(suffix.slice(0, i));
          if (i < suffix.length) after(TYPE_STEP_MS, typeNext);
          else after(500, finishStudentSubmit);
        };
        after(500, typeNext);
        return;
      }

      let i = 0;
      const typeNext = () => {
        if (cancelled) return;
        i += 1;
        setTyped(active.prompt.slice(0, i));
        if (i < active.prompt.length) after(TYPE_STEP_MS, typeNext);
        else after(700, runGenerating);
      };
      after(500, typeNext);
    };

    const finishStudentSubmit = () => {
      if (cancelled) return;
      setSubmitPressed(true);
      after(450, () => {
        if (cancelled) return;
        setSubmitPressed(false);
        runGenerating();
      });
    };

    const runGenerating = () => {
      if (cancelled) return;
      setStage("generating");
      // Student "check my work" runs quickly; AI generation ticks through phases.
      if (SCENES[current]!.kind === "student") {
        after(STUDENT_WORK_MS, runReveal);
        return;
      }
      let virtual = 0;
      setProgress(lessonAiPhaseAtElapsed(LESSON_AI_GENERATE_PHASES, 0));
      const tick = () => {
        if (cancelled) return;
        virtual += VIRTUAL_STEP_MS;
        setProgress(lessonAiPhaseAtElapsed(LESSON_AI_GENERATE_PHASES, virtual));
        if (virtual >= LAST_PHASE_MS + VIRTUAL_STEP_MS * 3) after(600, runReveal);
        else after(TICK_MS, tick);
      };
      after(TICK_MS, tick);
    };

    const runReveal = () => {
      if (cancelled) return;
      setStage("reveal");
      const hold = SCENES[current]!.holdMs;
      after(hold, () => {
        current = (current + 1) % SCENES.length;
        runTyping();
      });
    };

    runTyping();
    return () => {
      cancelled = true;
      while (timers.length > 0) {
        const id = timers.pop();
        if (id !== undefined) window.clearTimeout(id);
      }
    };
  }, [reduced, onScreen]);

  return (
    <div className="landing-ai-demo" ref={rootRef}>
      <div className="landing-ai-prompt">
        <p className="landing-ai-prompt-label">
          <FontAwesomeIcon icon={scene.buttonIcon} /> {scene.promptLabel}
        </p>
        {scene.kind === "ai" ? (
          <>
            <div className="landing-ai-prompt-box" aria-live="off">
              <span>{stage === "typing" ? typed : scene.prompt}</span>
              {stage === "typing" ? <span className="landing-ai-caret" aria-hidden="true" /> : null}
            </div>
            <span className="btn primary landing-ai-fake-btn" aria-hidden="true">
              <FontAwesomeIcon icon={scene.buttonIcon} /> {scene.buttonLabel}
            </span>
          </>
        ) : null}
      </div>

      <div
        className="landing-ai-stage"
        role="img"
        aria-label={
          scene.kind === "student" ? "Student lesson workspace demo" : "AI lesson studio demo"
        }
      >
        {/*
         * Every possible layer stays mounted in the same grid cell so the stage is
         * always as tall as the tallest state, no matter which scene is active. The
         * AI progress panel and the student "checking" panel are separate layers
         * (rather than a swapped conditional) so neither scene can shrink the stage.
         */}
        <div
          className="landing-ai-layer"
          data-visible={scene.kind === "ai" && stage !== "reveal"}
          aria-hidden={!(scene.kind === "ai" && stage !== "reveal")}
        >
          <div className="landing-ai-stage-panel">
            <LessonAiProgressPanel
              mode="generate"
              progress={stage === "generating" ? progress : FINISHED_PROGRESS}
            />
          </div>
        </div>
        <div
          className="landing-ai-layer"
          data-visible={scene.kind === "student" && (stage === "typing" || submitPressed)}
          aria-hidden={!(scene.kind === "student" && (stage === "typing" || submitPressed))}
        >
          <StudentCodingPanel
            codeBase={DEMO_STUDENT_WHALE_CODE_BASE}
            typedSuffix={
              scene.kind === "student" && (stage === "typing" || submitPressed)
                ? typed
                : DEMO_STUDENT_WHALE_TYPING
            }
            typing={scene.kind === "student" && stage === "typing"}
            submitPressed={scene.kind === "student" && submitPressed}
            buttonLabel="Check My Work"
          />
        </div>
        <div
          className="landing-ai-layer"
          data-visible={scene.kind === "student" && stage === "generating"}
          aria-hidden={!(scene.kind === "student" && stage === "generating")}
        >
          <CheckRunningPanel />
        </div>
        <div
          className="landing-ai-layer"
          data-visible={stage === "reveal" && scene.reveal === "draft"}
          aria-hidden={!(stage === "reveal" && scene.reveal === "draft")}
        >
          <DraftReveal draft={DEMO_AI_DRAFT} />
        </div>
        <div
          className="landing-ai-layer"
          data-visible={stage === "reveal" && scene.reveal === "plan"}
          aria-hidden={!(stage === "reveal" && scene.reveal === "plan")}
        >
          <PlanReveal />
        </div>
        <div
          className="landing-ai-layer"
          data-visible={stage === "reveal" && scene.reveal === "checks"}
          aria-hidden={!(stage === "reveal" && scene.reveal === "checks")}
        >
          <ChecksReveal
            active={stage === "reveal" && scene.reveal === "checks"}
            onAllPassed={onStudentChecksComplete}
          />
        </div>
      </div>
    </div>
  );
}

function DraftReveal({ draft }: { draft: LessonAiDraft }) {
  return (
    <section className="landing-ai-draft landing-ai-stage-panel">
      <div className="landing-ai-draft-head">
        <span className="landing-ai-draft-badge">
          <FontAwesomeIcon icon={faCheck} /> Draft ready
        </span>
        <div>
          <h3 className="landing-ai-draft-title">{draft.title}</h3>
          <p className="landing-ai-draft-desc muted">{draft.description}</p>
        </div>
      </div>
      <div className="landing-ai-draft-meta">
        <span>
          <FontAwesomeIcon icon={faListOl} /> {draft.tourSteps.length} guided steps
        </span>
        <span>
          <FontAwesomeIcon icon={faVial} /> {draft.tests.length} validation checks
        </span>
      </div>
      <ReadOnlyPythonCode
        code={draft.mainPy}
        className="landing-ai-draft-code"
        maxHeight="14rem"
        ariaLabel="AI-generated starter code"
      />
    </section>
  );
}

function StudentCodingPanel({
  codeBase,
  typedSuffix,
  typing,
  submitPressed,
  buttonLabel,
}: {
  codeBase: string;
  typedSuffix: string;
  typing: boolean;
  submitPressed: boolean;
  buttonLabel: string;
}) {
  const code = typedSuffix ? `${codeBase}${typedSuffix}` : codeBase;
  return (
    <section className="landing-student-code landing-ai-stage-panel">
      <div className="landing-student-code-toolbar">
        <div className="landing-student-code-head">
          <p className="teacher-ai-progress-eyebrow">Student workspace</p>
          <h3 className="landing-ai-draft-title">{DEMO_STUDENT_LESSON_TITLE}</h3>
        </div>
        <span
          className={`btn secondary landing-ai-fake-btn landing-student-submit${
            submitPressed ? " landing-student-submit--pressed" : ""
          }`}
          aria-hidden="true"
        >
          <FontAwesomeIcon icon={faCircleCheck} /> {buttonLabel}
        </span>
      </div>
      <ReadOnlyPythonCode
        code={code}
        className="landing-ai-draft-code landing-student-code-editor"
        minHeight="0"
        maxHeight="100%"
        scrollToEndOnChange={typing}
        ariaLabel="Student Python code"
      />
    </section>
  );
}

function CheckRunningPanel() {
  return (
    <section className="teacher-ai-progress landing-check-running landing-ai-stage-panel" role="status" aria-live="polite" aria-busy="true">
      <div className="teacher-ai-progress-header">
        <div>
          <p className="teacher-ai-progress-eyebrow">Student workspace</p>
          <h2 className="teacher-ai-progress-title">Checking your work</h2>
          <p className="teacher-ai-progress-lede muted">
            Running the lesson&apos;s automated checks right here in your browser…
          </p>
        </div>
        <div className="teacher-ai-progress-spinner" aria-hidden="true" />
      </div>
    </section>
  );
}

function ChecksReveal({
  active,
  onAllPassed,
}: {
  active: boolean;
  onAllPassed: () => void;
}) {
  const baselinePassed = DEMO_STUDENT_CHECKS.filter((c) => c.passed).length;
  const [passedCount, setPassedCount] = useState(baselinePassed);

  useEffect(() => {
    if (!active) {
      setPassedCount(baselinePassed);
      return;
    }

    setPassedCount(baselinePassed);
    const timers: number[] = [];
    const after = (ms: number, fn: () => void) => {
      timers.push(window.setTimeout(fn, ms));
    };

    after(450, () => setPassedCount(baselinePassed + 1));
    after(950, () => setPassedCount(baselinePassed + 2));
    after(1450, () => {
      setPassedCount(DEMO_STUDENT_CHECKS.length);
      onAllPassed();
    });

    return () => {
      while (timers.length > 0) {
        const id = timers.pop();
        if (id !== undefined) window.clearTimeout(id);
      }
    };
  }, [active, baselinePassed, onAllPassed]);

  const total = DEMO_STUDENT_CHECKS.length;
  const allPassed = passedCount === total;

  return (
    <section className="landing-ai-checks landing-ai-stage-panel">
      <div className="landing-ai-draft-head">
        <span
          className={`landing-ai-draft-badge${
            allPassed ? " landing-ai-checks-badge--pass" : " landing-ai-checks-badge"
          }`}
        >
          <FontAwesomeIcon icon={faListCheck} /> {passedCount} of {total} checks passing
        </span>
        <div>
          <h3 className="landing-ai-draft-title">{DEMO_STUDENT_LESSON_TITLE}</h3>
          <p className="landing-ai-draft-desc muted">
            {allPassed
              ? "Every check passed! The whale spouts and the lesson is complete."
              : "Running your lesson checks…"}
          </p>
        </div>
      </div>
      <ul className="landing-ai-check-list">
        {DEMO_STUDENT_CHECKS.map((c, index) => {
          const passed = index < passedCount;
          return (
            <li
              key={c.name}
              className={`landing-ai-check landing-ai-check--${passed ? "pass" : "todo"}`}
            >
              <span className="landing-ai-check-marker" aria-hidden="true">
                {passed ? <FontAwesomeIcon icon={faCheck} /> : null}
              </span>
              <span className="landing-ai-check-name">{c.name}</span>
              <span className="landing-ai-check-status">{passed ? "Passed" : "To do"}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function PlanReveal() {
  return (
    <section className="landing-ai-plan landing-ai-stage-panel">
      <div className="landing-ai-draft-head">
        <span className="landing-ai-draft-badge">
          <FontAwesomeIcon icon={faCheck} /> Added to plan
        </span>
        <div>
          <h3 className="landing-ai-draft-title">Period 3 · lesson plan updated</h3>
          <p className="landing-ai-draft-desc muted">
            A Moby Dick&ndash;themed classes lesson, inserted right where this cohort needs it.
          </p>
        </div>
      </div>
      <ol className="landing-ai-plan-list">
        {DEMO_COHORT_PLAN.map((item, index) => (
          <PlanRow key={item.id} title={item.title} iconName={item.iconName} bg={item.bgColor} fg={item.fgColor}>
            {index === DEMO_PLAN_INSERT_AFTER ? (
              <>
                <li className="landing-ai-plan-insert" aria-hidden="true">
                  <FontAwesomeIcon icon={faArrowDown} /> inserted here
                </li>
                <PlanRow
                  title={DEMO_PLAN_INSERTED.title}
                  iconName={DEMO_PLAN_INSERTED.iconName}
                  bg={DEMO_PLAN_INSERTED.bgColor}
                  fg={DEMO_PLAN_INSERTED.fgColor}
                  isNew
                />
              </>
            ) : null}
          </PlanRow>
        ))}
      </ol>
    </section>
  );
}

function PlanRow({
  title,
  iconName,
  bg,
  fg,
  isNew,
  children,
}: {
  title: string;
  iconName: string;
  bg: string;
  fg: string;
  isNew?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <>
      <li className={`landing-ai-plan-row${isNew ? " landing-ai-plan-row--new" : ""}`}>
        <LessonIcon lessonId={title} title={title} iconName={iconName} bgColor={bg} fgColor={fg} size={2} />
        <span className="landing-ai-plan-row-title">{title}</span>
        {isNew ? <span className="landing-ai-plan-new-badge">New</span> : null}
      </li>
      {children}
    </>
  );
}
